import React, { useEffect, useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import BottomSheet from "@/components/BottomSheet";
import {
  formatRupiahFull,
  formatTanggalID,
  formatBulanID,
  currentBulan,
} from "@/lib/format";
import { Copy, Download, FileText, X } from "lucide-react";

/**
 * Riwayat Bayar Hutang - bottom sheet
 * Filter: Bulan Ini | Semua
 * Actions: Salin untuk share (WhatsApp), Download CSV
 */
export default function RiwayatBayarHutang({ open, onClose }) {
  const [scope, setScope] = useState("bulan");
  const [bulan] = useState(currentBulan());
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [manualCopyText, setManualCopyText] = useState(null);
  const manualTextareaRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const q =
      scope === "bulan" ? `scope=bulan&bulan=${bulan}` : "scope=all";
    api
      .get(`/pembayaran-hutang?${q}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Gagal memuat data"))
      .finally(() => setLoading(false));
  }, [open, scope, bulan]);

  const scopeLabel = useMemo(
    () => (scope === "bulan" ? formatBulanID(bulan) : "Semua Bulan"),
    [scope, bulan]
  );

  const buildShareText = () => {
    const lines = [];
    lines.push(`💳 Riwayat Bayar Hutang — ${scopeLabel}`);
    lines.push("─────────────────────");
    data.items.forEach((it, i) => {
      lines.push(
        `${i + 1}. ${it.hutang_deskripsi}\n   ${formatTanggalID(
          it.tanggal
        )} — ${formatRupiahFull(it.nominal)}`
      );
    });
    lines.push("─────────────────────");
    lines.push(`Total: ${formatRupiahFull(data.total)}`);
    lines.push(`(${data.items.length} pembayaran)`);
    return lines.join("\n");
  };

  const onCopy = async () => {
    if (data.items.length === 0) {
      toast.error("Tidak ada data untuk disalin");
      return;
    }
    const text = buildShareText();

    // 1. Try modern Clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Tersalin! Siap paste ke WhatsApp.");
        return;
      } catch {
        // fall through
      }
    }

    // 2. Try execCommand fallback
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "0";
      ta.style.width = "1px";
      ta.style.height = "1px";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) {
        toast.success("Tersalin! Siap paste ke WhatsApp.");
        return;
      }
    } catch {
      // fall through
    }

    // 3. Ultimate fallback: show text in modal for manual copy
    setManualCopyText(text);
    toast.info("Silakan salin manual dari kotak yang muncul.");
  };

  const onManualCopyRetry = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(manualCopyText);
        toast.success("Tersalin!");
        setManualCopyText(null);
        return;
      }
    } catch {
      /* ignore */
    }
    // Select textarea content
    if (manualTextareaRef.current) {
      manualTextareaRef.current.focus();
      manualTextareaRef.current.select();
      try {
        const ok = document.execCommand("copy");
        if (ok) {
          toast.success("Tersalin!");
          setManualCopyText(null);
          return;
        }
      } catch {
        /* ignore */
      }
    }
    toast.error("Salin gagal — tekan lama teks lalu pilih Copy.");
  };

  const onDownloadCSV = () => {
    if (data.items.length === 0) {
      toast.error("Tidak ada data untuk diekspor");
      return;
    }
    const header = "No,Nama Hutang,Status,Tanggal Bayar,Nominal,Rekening\n";
    const rows = data.items
      .map((it, i) =>
        [
          i + 1,
          `"${it.hutang_deskripsi.replace(/"/g, '""')}"`,
          it.hutang_status,
          it.tanggal,
          it.nominal,
          `"${(it.rekening_nama || "").replace(/"/g, '""')}"`,
        ].join(",")
      )
      .join("\n");
    const csv = header + rows + `\n,,,,${data.total},TOTAL\n`;
    // Add UTF-8 BOM so Excel opens Indonesian chars correctly
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const suffix =
      scope === "bulan" ? bulan : "semua";
    a.href = url;
    a.download = `bayar-hutang-${suffix}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("CSV terunduh");
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Riwayat Bayar Hutang"
      testId="riwayat-bayar-sheet"
    >
      {/* Filter chips */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setScope("bulan")}
          data-testid="riwayat-filter-bulan"
          className={`flex-1 h-9 rounded-lg text-xs font-semibold tap ${
            scope === "bulan"
              ? "text-white"
              : "bg-slate-100 text-slate-600"
          }`}
          style={{
            backgroundColor: scope === "bulan" ? "#1F4E5F" : undefined,
          }}
        >
          Bulan Ini ({formatBulanID(bulan)})
        </button>
        <button
          onClick={() => setScope("all")}
          data-testid="riwayat-filter-all"
          className={`flex-1 h-9 rounded-lg text-xs font-semibold tap ${
            scope === "all"
              ? "text-white"
              : "bg-slate-100 text-slate-600"
          }`}
          style={{
            backgroundColor: scope === "all" ? "#1F4E5F" : undefined,
          }}
        >
          Semua
        </button>
      </div>

      {/* Total */}
      <div
        className="rounded-xl p-3 mb-3 flex items-center justify-between"
        style={{ backgroundColor: "#FCE4D6" }}
        data-testid="riwayat-total-box"
      >
        <div>
          <div className="text-[10px] font-semibold text-slate-700">
            Total {scope === "bulan" ? formatBulanID(bulan) : "keseluruhan"}
          </div>
          <div
            className="text-base font-extrabold mt-0.5"
            style={{ color: "#B45309" }}
          >
            {formatRupiahFull(data.total || 0)}
          </div>
        </div>
        <div className="text-[10px] text-slate-500">
          {data.items.length} pembayaran
        </div>
      </div>

      {/* List */}
      <div className="max-h-[38vh] overflow-y-auto" data-testid="riwayat-list">
        {loading ? (
          <div className="text-center text-xs text-slate-500 py-6">
            Memuat...
          </div>
        ) : data.items.length === 0 ? (
          <div className="text-center text-xs text-slate-500 py-6 bg-slate-50 rounded-xl border border-slate-100">
            Belum ada pembayaran{" "}
            {scope === "bulan" ? `di ${formatBulanID(bulan)}` : ""}.
          </div>
        ) : (
          <div className="space-y-1.5">
            {data.items.map((it, i) => (
              <div
                key={i}
                className="bg-slate-50 rounded-lg p-2.5 flex items-start justify-between gap-2 border border-slate-100"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold text-slate-900 truncate">
                    {it.hutang_deskripsi}
                    {it.hutang_status === "lunas" && (
                      <span
                        className="ml-1.5 text-[8px] px-1 py-0.5 rounded font-bold text-white"
                        style={{ backgroundColor: "#375623" }}
                      >
                        LUNAS
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {formatTanggalID(it.tanggal)} · {it.rekening_nama}
                  </div>
                  {it.keterangan && (
    <div className="text-[10px] text-slate-400 mt-0.5 italic">
        {it.keterangan}
    </div>
)}
                </div>
                <div
                  className="text-xs font-extrabold whitespace-nowrap"
                  style={{ color: "#C00000" }}
                >
                  {formatRupiahFull(it.nominal)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          onClick={onCopy}
          disabled={data.items.length === 0}
          data-testid="riwayat-copy-btn"
          className="h-11 rounded-xl bg-white border border-slate-200 text-[#1F4E5F] font-semibold text-sm tap flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          <Copy size={14} /> Salin
        </button>
        <button
          onClick={onDownloadCSV}
          disabled={data.items.length === 0}
          data-testid="riwayat-csv-btn"
          className="h-11 rounded-xl text-white font-semibold text-sm tap flex items-center justify-center gap-1.5 disabled:opacity-50"
          style={{ backgroundColor: "#1F4E5F" }}
        >
          <Download size={14} /> CSV
        </button>
      </div>
      <p className="mt-3 text-[10px] text-slate-400 text-center flex items-center justify-center gap-1">
        <FileText size={10} /> Salin untuk share via WhatsApp/Telegram
      </p>

      {/* Manual copy fallback modal */}
      {manualCopyText && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center px-4 fade-anim"
          data-testid="manual-copy-modal"
        >
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setManualCopyText(null)}
          />
          <div className="relative w-full max-w-[340px] bg-white rounded-2xl shadow-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-900">Salin Manual</h3>
              <button
                onClick={() => setManualCopyText(null)}
                data-testid="manual-copy-close"
                className="text-slate-400 tap"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mb-2">
              Tekan lama teks di bawah lalu pilih <b>Copy / Salin</b>, atau klik tombol Salin.
            </p>
            <textarea
              ref={manualTextareaRef}
              readOnly
              value={manualCopyText}
              data-testid="manual-copy-textarea"
              onFocus={(e) => e.target.select()}
              className="w-full h-48 p-2 text-[11px] font-mono border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:border-[#1F4E5F] resize-none"
            />
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => setManualCopyText(null)}
                data-testid="manual-copy-cancel"
                className="flex-1 tap py-2 rounded-lg bg-slate-100 text-slate-700 font-semibold text-xs"
              >
                Tutup
              </button>
              <button
                onClick={onManualCopyRetry}
                data-testid="manual-copy-retry"
                className="flex-1 tap py-2 rounded-lg font-semibold text-xs text-white"
                style={{ backgroundColor: "#1F4E5F" }}
              >
                Coba Salin
              </button>
            </div>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}
